import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreInput,
  OreStack,
  OreText,
  SvgEdit,
} from '@edenredespana/oreneta'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { handleErrors } from '@/Shared/ui/form'
import { EmailChangeDataForm } from '@/User/domain'
import { createApiUserRepository } from '@/User/infrastructure'
import { useUserData } from '@/User/ui/context'
import { useChangeEmail } from '@/User/ui/hooks'
import { userDataTranslation } from '@/User/ui/userTranslation'

export const MyUserData = (): JSX.Element => {
  const { t } = useTranslation()

  const [isEditing, setIsEditing] = useState(false)

  const {
    user: { user },
    getUserData,
    userName,
  } = useUserData()

  const {
    form: { register, formState, handleSubmit },
    save,
  } = useChangeEmail(createApiUserRepository(), user?.email ?? '')

  const { errors } = handleErrors(formState)

  const handleEmailChange = async (data: EmailChangeDataForm): Promise<void> => {
    save(data).then(valid => {
      if (valid) {
        setIsEditing(false)
        getUserData()
      }
    })
  }

  return (
    <OreCardContainer>
      <OreCardWrapper space="large">
        <form>
          <OreHeading size="headline-sm" as="h2">
            {t(userDataTranslation.title)}
          </OreHeading>
          <OreDivider space="medium" />
          <OreStack placeContent="stretch" space="large">
            <OreStack space="xsmall">
              <OreText bold>{t(userDataTranslation.name.label)}</OreText>
              <OreText data-testid="merchantIdentityName">{`${user?.name} ${user?.surname} ${user?.surname2 ? user.surname2 : ''}`}</OreText>
            </OreStack>

            <OreStack space="xsmall">
              <OreText bold>{t(userDataTranslation.userName.label)}</OreText>
              <OreText data-testid="userName">{userName}</OreText>
            </OreStack>

            <OreInput
              {...register('email')}
              {...errors('email')}
              id="email"
              type="email"
              label={t(userDataTranslation.email.label)}
              disabled={!isEditing}
              maxLength={255}
            />

            <OreStack placeContent="start">
              {!isEditing ? (
                <OreButton
                  as="button"
                  category="secondary"
                  icon={<SvgEdit />}
                  onClick={(): void => setIsEditing(true)}
                  size="large"
                  data-testid="editEmail"
                >
                  {t(userDataTranslation.edit.label)}
                </OreButton>
              ) : (
                <OreButton
                  category="primary"
                  icon={<SvgEdit />}
                  onClick={handleSubmit(handleEmailChange)}
                  size="large"
                  data-testid="saveEmail"
                >
                  {t(userDataTranslation.save.label)}
                </OreButton>
              )}
            </OreStack>
          </OreStack>
        </form>
      </OreCardWrapper>
    </OreCardContainer>
  )
}
